import { useState } from "react";
import { Checkbox, Input, Modal, Select } from "antd";
import { BiChevronLeft } from "react-icons/bi";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useNavigate, useParams } from "react-router";
import { getCase, updateCase } from "../../api/monitoring";
import { showErrorToast, showSuccessToast } from "../../utils/toast";
import { usePermissionEffect } from "../../utils/usePermissionEffect";
import CustomerLink from "../shared/CustomerLink";
import { OUTCOME_LABELS } from "./cases";
import { HOLD_LABELS, Pill, STATUS_LABELS, SeverityBadge } from "./shared";

const btn = "rounded-[5px] px-4 py-2 font-bold text-[13px] disabled:opacity-50 whitespace-nowrap";

const CaseDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { getModulePermission } = usePermissionEffect();
  const canWork = getModulePermission("monitoring", "canUpdate");
  const [note, setNote] = useState("");
  const [closeOpen, setCloseOpen] = useState(false);
  const [outcome, setOutcome] = useState();
  const [restrict, setRestrict] = useState(false);
  const [closeNote, setCloseNote] = useState("");

  const { data: c } = useQuery(["monitoring-case", id], () => getCase(id));
  const mutation = useMutation(updateCase, {
    onSuccess: () => {
      showSuccessToast("Case updated");
      queryClient.invalidateQueries(["monitoring-case", id]);
      queryClient.invalidateQueries("monitoring-cases");
      queryClient.invalidateQueries("monitoring-alerts");
      queryClient.invalidateQueries("user-detail");
    },
    onError: (e) => showErrorToast(e.message),
  });

  if (!c) return <div className="text-[14px] text-[#00000080]">Loading…</div>;
  const anyHeld = c.alerts.some((a) => a.hold?.state === "held");

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <BiChevronLeft className="text-[25px] cursor-pointer" onClick={() => navigate(-1)} />
        <div className="font-bold text-[18px]">{c.title}</div>
        <Pill className={c.status === "open" ? "bg-[#F2C94C33] text-[#B7791F]" : "bg-[#0000000D] text-[#4F4F4F]"}>{c.status}</Pill>
        {c.outcome && <span className="text-[13px] text-[#00000080]">{OUTCOME_LABELS[c.outcome]}</span>}
        {canWork && (
          <div className="ml-auto flex gap-2">
            {c.status === "open" ? (
              <button type="button" className={`bg-dark text-white ${btn}`} onClick={() => setCloseOpen(true)}>
                Close case
              </button>
            ) : (
              <button type="button" className={`bg-[#0000000D] ${btn}`} onClick={() => mutation.mutate({ id, status: "open" })}>
                Reopen
              </button>
            )}
          </div>
        )}
      </div>

      <div className="flex gap-4 items-start">
        <div className="w-2/3 space-y-4">
          <div className="bg-white rounded-lg p-5 space-y-2">
            <div className="font-bold text-[14px]">Alerts in this case</div>
            {c.alerts.map((a) => (
              <div
                key={a._id}
                className="flex items-start gap-3 text-[13px] border-b border-[#F2F2F2] py-2 cursor-pointer hover:bg-gray-50"
                onClick={() => navigate(`/monitoring/alerts/${a._id}`)}
              >
                <SeverityBadge severity={a.severity} />
                <div className="flex-1">
                  <div className="font-bold">{a.ruleName}</div>
                  <div className="text-[#00000099]">{a.summary}</div>
                </div>
                <div className="text-right">
                  <div>{STATUS_LABELS[a.status]}</div>
                  {a.hold?.state && <div className="text-[12px] text-[#EB5757]">{HOLD_LABELS[a.hold.state]}</div>}
                </div>
              </div>
            ))}
          </div>

          <div className="bg-white rounded-lg p-5 space-y-3">
            <div className="font-bold text-[14px]">Notes</div>
            {c.notes.map((n, i) => (
              <div key={i} className="text-[13px] border-b border-[#F2F2F2] pb-2">
                <div>{n.text}</div>
                <div className="text-[12px] text-[#00000080]">
                  {n.by} · {new Date(n.at).toLocaleString()}
                </div>
              </div>
            ))}
            {canWork && (
              <div className="flex gap-2">
                <Input.TextArea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Findings, calls made, documents requested…" />
                <button
                  type="button"
                  disabled={!note.trim() || mutation.isLoading}
                  className={`bg-[#0000000D] ${btn}`}
                  onClick={() => mutation.mutate({ id, note }, { onSuccess: () => setNote("") })}
                >
                  Add
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="w-1/3 space-y-4">
          <div className="bg-white rounded-lg p-5 space-y-2 text-[13px]">
            <div className="font-bold text-[14px]">Customer</div>
            <CustomerLink userId={c.userId} record={{ user: c.customer }} />
            <div className="text-[#00000080]">{c.customer?.email}</div>
            <div className="pt-2">Owner: {c.assignee || "—"}</div>
            <div>Opened by {c.createdBy} on {new Date(c.createdAt).toLocaleDateString()}</div>
            {c.closedAt && <div>Closed by {c.closedBy} on {new Date(c.closedAt).toLocaleDateString()}</div>}
            {canWork && c.status === "open" && (
              <button type="button" className={`bg-[#0000000D] ${btn} mt-2`} onClick={() => mutation.mutate({ id, assignee: "me" })}>
                Take ownership
              </button>
            )}
          </div>
          <div className="bg-white rounded-lg p-5 space-y-2">
            <div className="font-bold text-[14px]">Timeline</div>
            {c.timeline.map((t) => (
              <div key={t._id} className="text-[12px] border-b border-[#F2F2F2] pb-1">
                <span className="font-bold">{t.action}</span> · {t.actor}
                <div className="text-[#00000080]">{new Date(t.at).toLocaleString()}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <Modal
        title="Close case"
        open={closeOpen}
        onCancel={() => setCloseOpen(false)}
        okText="Close case"
        okButtonProps={{ disabled: !outcome || mutation.isLoading }}
        onOk={() =>
          mutation.mutate(
            { id, status: "closed", outcome, restrictAccount: restrict, ...(closeNote.trim() && { note: closeNote }) },
            { onSuccess: () => setCloseOpen(false) },
          )
        }
      >
        <div className="space-y-3 py-2">
          <Select
            placeholder="Outcome"
            value={outcome}
            onChange={setOutcome}
            className="w-full"
            options={Object.entries(OUTCOME_LABELS).map(([value, label]) => ({ value, label }))}
          />
          <Checkbox checked={restrict} onChange={(e) => setRestrict(e.target.checked)}>
            Deactivate the customer's account
          </Checkbox>
          <Input.TextArea rows={2} value={closeNote} onChange={(e) => setCloseNote(e.target.value)} placeholder="Closing note (optional)" />
          {anyHeld && (
            <div className="text-[12px] text-[#EB5757]">
              A payment in this case is still held. Release or reject it from its alert; closing the case won't do that.
            </div>
          )}
          <div className="text-[12px] text-[#00000080]">Open alerts in this case will be marked resolved.</div>
        </div>
      </Modal>
    </div>
  );
};

export default CaseDetail;
